import { z } from 'zod'
import type { ProductWarehouseRef, ReorderStatus } from './catalog.js'
import { quantitySchema, versionSchema } from './common.js'
import type { StockReason } from './inventory.js'
import type { ReportVariant } from './operational-reports.js'
import type { SupplierRef } from './purchasing.js'

/**
 * Stock insights (IA1; docs/refactor/INVENTORY_ASSISTANT.md §3.1): rule-based figures over the stock ledger and
 * its projections. No AI is involved and nothing is written; the same question gives the same answer. Every
 * route needs `inventory.read` (cost and value included, Q10) and the Inventory module. Goods only: active
 * variants of active products. Dates are business dates (Asia/Muscat); quantities carry up to 4 decimals, money
 * 3, unit costs 6. The inventory assistant reads these through its tools.
 *
 * With `warehouseId` the figures are that warehouse's (its on hand, its issues); without, all warehouses.
 * Reorder levels are per variant (all warehouses), so a warehouse view compares its own quantity with them.
 */

// ── the rules' parameters (fixed in IA1, decision 5) ──────────────────────────

/** Usage is counted over this many days back from today (today included). */
export const STOCK_USAGE_DAYS = 90
/** Usage per day divides by the days the item moved in inside the window, but never fewer than this. */
export const STOCK_USAGE_MIN_DAYS = 7
/** An item with this many days of cover or fewer is running out. */
export const STOCK_COVER_WARN_DAYS = 14
/** A reorder stocks up to this many days of usage unless the reader asks for another target. */
export const STOCK_REORDER_TARGET_DAYS = 30
export const STOCK_REORDER_TARGET_MIN_DAYS = 7
export const STOCK_REORDER_TARGET_MAX_DAYS = 180
/** A suggested reorder level covers this many days of usage. */
export const STOCK_REORDER_LEVEL_DAYS = 14
/** Held with value and nothing sold or issued for this many days: slow. */
export const STOCK_SLOW_DAYS = 90
/** Held with value and no movement at all for this many days: dead. */
export const STOCK_DEAD_DAYS = 180
/** Write-offs are summed over this many days back from today… */
export const STOCK_WRITE_OFF_DAYS = 30
/** …and found high above this share (per cent) of the stock value. */
export const STOCK_WRITE_OFF_PCT = 2
/** A draft transfer older than this many days. */
export const STOCK_OLD_DRAFT_DAYS = 14
/** The items a check, the reorder list's default page and the overview's top list carry. */
export const STOCK_INSIGHT_TOP = 20

/** The movement reasons that count as usage: sold or issued to a project, less what came back. */
export const STOCK_USAGE_REASONS = [
  'SALE',
  'SALE_RETURN',
  'PROJECT_ISSUE',
  'PROJECT_RETURN',
] as const satisfies readonly StockReason[]

/**
 * How fast an item goes: `quantity` is the net units sold or issued to projects from `from` to `to` (never
 * below zero); `perDay` divides it by `days`, 4 decimals. `days` are the days the item has been in the scope's
 * ledger, from its first movement ever to `to`, capped at the window (`STOCK_USAGE_DAYS`) and never fewer than
 * `STOCK_USAGE_MIN_DAYS`: an item first stocked three weeks ago is divided by three weeks, an old item always
 * by the whole window (IA1 review: a recent sale of an old item must not look like fast usage).
 */
export interface StockUsage {
  from: string
  to: string
  days: number
  quantity: string
  perDay: string
}

const scope = { warehouseId: z.uuid().optional() }

// ── overview (`GET /stock/insights/overview`) ─────────────────────────────────

export const stockOverviewQuerySchema = z.object({ ...scope })
export type StockOverviewQuery = z.input<typeof stockOverviewQuerySchema>

export interface StockOverview {
  asOf: string
  /** The warehouse asked for; null: all. */
  warehouse: ProductWarehouseRef | null
  /** Value at cost of the stock in the scope: Σ on hand × the variant's weighted-average cost (3 dp). */
  value: string
  /**
   * Variants in the scope. `stocked`: quantity above zero. `low`: quantity above zero and at or below a reorder
   * level above zero. `out`: quantity zero with at least one movement ever (never-stocked catalogue items are
   * not counted). `negative`: below zero in a warehouse of the scope.
   */
  counts: { stocked: number; low: number; out: number; negative: number }
  /** Every active warehouse (the main one first), whatever the scope. */
  warehouses: { warehouse: ProductWarehouseRef; isMain: boolean; value: string; stocked: number }[]
  /** The ten variants holding the most value in the scope, largest first. */
  topByValue: { variant: ReportVariant; onHand: string; value: string }[]
}

// ── one item (`GET /stock/insights/item`) ─────────────────────────────────────

export const stockItemInsightQuerySchema = z.object({ variantId: z.uuid(), ...scope })
export type StockItemInsightQuery = z.input<typeof stockItemInsightQuerySchema>

/** The latest posted purchase line of a variant: who sold it and its ex-VAT unit cost after discount (6 dp). */
export interface StockLastPurchase {
  date: string
  purchase: { id: string; number: string | null }
  /** Null for a reader without `suppliers.read` (G26), with `supplierHidden: true`. */
  supplier: SupplierRef | null
  supplierHidden?: boolean
  unitCost: string
}

/** One movement reason over the usage window: what came in, what went out (both positive) and how many movements. */
export interface StockReasonTotal {
  reason: StockReason
  quantityIn: string
  quantityOut: string
  count: number
}

/**
 * One variant (404 when it is not a stocked good of the company; an archived one is still answered while it
 * holds stock or history).
 */
export interface StockItemInsight {
  asOf: string
  variant: ReportVariant
  /** Whole units only unless true (the unit's `allowFractions`). */
  allowFractions: boolean
  /** The warehouse asked for; null: all. */
  warehouse: ProductWarehouseRef | null
  /** In the scope. */
  onHand: string
  /**
   * Units not received yet that the reorder rule already counts on: the goods lines of open draft purchases
   * (the company's purchase orders; lines delivered to a project site are left out), for the scope's
   * warehouse (a draft without a warehouse arrives at the main one); with `warehouseId`, also the lines of
   * draft transfers into it.
   */
  onOrder: string
  reorderLevel: string
  /**
   * NEGATIVE: below zero in a warehouse of the scope; OUT: nothing on hand; LOW: at or below a reorder level
   * above zero; OK otherwise.
   */
  status: ReorderStatus
  /** The variant's weighted-average cost over all warehouses (6 dp) and the scope's on hand at it (3 dp). */
  averageCost: string
  value: string
  /** Every warehouse where the variant has a balance row, the main one first (whatever the scope). */
  warehouses: { warehouse: ProductWarehouseRef; onHand: string }[]
  usage: StockUsage
  /** onHand ÷ usage per day, 1 decimal; null without usage; "0.0" when nothing is on hand. */
  daysOfCover: string | null
  /**
   * On hand with `STOCK_COVER_WARN_DAYS` of cover or fewer (the RUNNING_OUT check's own rule), whatever the
   * status says: the screen shows it beside an item that is still above its reorder level.
   */
  runningOut: boolean
  /** The usage window's movements in the scope, by reason (only reasons that occurred). */
  movements: StockReasonTotal[]
  /** The latest movement into, out of, and of any kind in the scope (dates), null when none ever. */
  lastReceiptDate: string | null
  lastIssueDate: string | null
  lastMovementDate: string | null
  /** Only with `purchasing.read` over all purchases (ADR-025); null otherwise or when never bought. */
  lastPurchase: StockLastPurchase | null
  /**
   * What the reorder rule suggests, null without usage: a reorder level covering `STOCK_REORDER_LEVEL_DAYS` of
   * usage, and the order quantity of `StockReorderRow.suggestedQuantity` at `STOCK_REORDER_TARGET_DAYS` (what
   * brings on hand and on order up to the reorder level plus the target days of usage); "0" when nothing is
   * needed.
   */
  suggestion: { reorderLevel: string; orderQuantity: string } | null
}

// ── stock checks (`GET /stock/insights/checks`) ───────────────────────────────

export const STOCK_CHECK_CODES = [
  'NEGATIVE_STOCK',
  'OUT_OF_STOCK_IN_DEMAND',
  'RUNNING_OUT',
  'LOW_STOCK',
  'TRANSFER_CANDIDATE',
  'NO_REORDER_LEVEL',
  'SLOW_MOVING',
  'DEAD_STOCK',
  'HIGH_WRITE_OFFS',
  'OLD_DRAFT_TRANSFERS',
] as const
export type StockCheckCode = (typeof STOCK_CHECK_CODES)[number]

export const STOCK_CHECK_SEVERITIES = ['critical', 'warning', 'info'] as const
export type StockCheckSeverity = (typeof STOCK_CHECK_SEVERITIES)[number]

/**
 * What each check finds (INVENTORY_ASSISTANT.md §3.1). An item is reported by the first of NEGATIVE_STOCK,
 * OUT_OF_STOCK_IN_DEMAND, RUNNING_OUT and LOW_STOCK that fits it, so these four never repeat an item; the
 * others stand on their own.
 */
export const STOCK_CHECK_DEFS: Record<StockCheckCode, { severity: StockCheckSeverity }> = {
  /**
   * A variant below zero in a warehouse (one item per variant and warehouse). `value` is the quantity below
   * zero at the average cost, as a positive amount.
   */
  NEGATIVE_STOCK: { severity: 'critical' },
  /** Nothing on hand in the scope, with usage in the window. */
  OUT_OF_STOCK_IN_DEMAND: { severity: 'critical' },
  /** On hand, with `STOCK_COVER_WARN_DAYS` of cover or fewer. */
  RUNNING_OUT: { severity: 'warning' },
  /**
   * At or below a reorder level above zero, nothing on hand included (an item out of stock without recent
   * sales is low, not "in demand"). With `warehouseId`, only variants that warehouse holds or once held (it has
   * a balance row): levels are per variant, so a branch that never stocked an item is not low.
   */
  LOW_STOCK: { severity: 'warning' },
  /**
   * Zero or below in one active warehouse while another active warehouse holds the variant (one item per short
   * warehouse; `otherWarehouse` is the one holding the most). Needs two active warehouses. It carries no `value`.
   */
  TRANSFER_CANDIDATE: { severity: 'info' },
  /** Usage in the window and a reorder level of zero. */
  NO_REORDER_LEVEL: { severity: 'info' },
  /**
   * On hand with value, first stocked `STOCK_SLOW_DAYS` ago or earlier (so a newly received item is not slow),
   * nothing sold or issued to a project (`SALE`, `PROJECT_ISSUE`) for `STOCK_SLOW_DAYS`, and some movement
   * within `STOCK_DEAD_DAYS`.
   */
  SLOW_MOVING: { severity: 'info' },
  /** On hand with value and no movement of any kind for `STOCK_DEAD_DAYS`. */
  DEAD_STOCK: { severity: 'warning' },
  /**
   * The posted adjustments' and counts' decreases of the last `STOCK_WRITE_OFF_DAYS` days, at cost, above
   * `STOCK_WRITE_OFF_PCT` per cent of the scope's stock value. Its items are the variants written off, the
   * largest value first; `value` is what was written off.
   */
  HIGH_WRITE_OFFS: { severity: 'warning' },
  /** Draft transfers created more than `STOCK_OLD_DRAFT_DAYS` days ago (out of or into the scope's warehouse). */
  OLD_DRAFT_TRANSFERS: { severity: 'info' },
}

export const stockChecksQuerySchema = z.object({ ...scope })
export type StockChecksQuery = z.input<typeof stockChecksQuerySchema>

/** One thing a check found: an item (in a warehouse, for the per-warehouse checks) or a draft transfer. */
export interface StockCheckItem {
  /** Null only for OLD_DRAFT_TRANSFERS. */
  variant: ReportVariant | null
  /** The warehouse the row is about; null when it is about all warehouses. */
  warehouse: ProductWarehouseRef | null
  /** TRANSFER_CANDIDATE: the warehouse holding the item, and how much it holds. */
  otherWarehouse: ProductWarehouseRef | null
  otherOnHand: string | null
  onHand: string | null
  reorderLevel: string | null
  /**
   * Value at cost of the on hand above zero, never negative ("0.000" when nothing is held); for
   * NEGATIVE_STOCK, of the quantity below zero (positive); for HIGH_WRITE_OFFS, what was written off; null
   * for TRANSFER_CANDIDATE and OLD_DRAFT_TRANSFERS.
   */
  value: string | null
  usagePerDay: string | null
  daysOfCover: string | null
  lastMovementDate: string | null
  /** OLD_DRAFT_TRANSFERS: the draft, labelled by its two warehouse codes, and the day it was created. */
  transfer: { id: string; label: string; createdOn: string } | null
}

export interface StockCheckFinding {
  code: StockCheckCode
  severity: StockCheckSeverity
  /** Everything the check found (items may carry fewer). */
  count: number
  /** Σ `value` of everything found (3 dp); null for TRANSFER_CANDIDATE and OLD_DRAFT_TRANSFERS. */
  value: string | null
  /** The thresholds the check used (`days`, `pct`, `stockValue`), for the wording. */
  params: Record<string, string | number | null>
  /** The first `STOCK_INSIGHT_TOP`, the worst first. */
  items: StockCheckItem[]
}

/** Checks that found nothing are left out. Ordered critical, warning, info, then as `STOCK_CHECK_CODES`. */
export interface StockChecks {
  asOf: string
  warehouse: ProductWarehouseRef | null
  usageDays: number
  findings: StockCheckFinding[]
  /** Findings (checks that found something) per severity. */
  counts: Record<StockCheckSeverity, number>
}

// ── reorder suggestions (`GET /stock/insights/reorder`) ───────────────────────

export const stockReorderQuerySchema = z.object({
  ...scope,
  /** Stock up to this many days of usage. */
  targetDays: z.coerce
    .number()
    .int()
    .min(STOCK_REORDER_TARGET_MIN_DAYS)
    .max(STOCK_REORDER_TARGET_MAX_DAYS)
    .default(STOCK_REORDER_TARGET_DAYS),
  /** Only the items last bought from this supplier (needs `purchasing.read` over all purchases; else none). */
  supplierId: z.uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(STOCK_INSIGHT_TOP),
})
export type StockReorderQuery = z.input<typeof stockReorderQuerySchema>

/** Why an item is on the reorder list: the first that fits. */
export const STOCK_REORDER_REASONS = ['NEGATIVE', 'OUT', 'RUNNING_OUT', 'LOW'] as const
export type StockReorderReason = (typeof STOCK_REORDER_REASONS)[number]

export interface StockReorderRow {
  variant: ReportVariant
  allowFractions: boolean
  /** The product tracks serial numbers: its document lines need one per unit when posted. */
  trackSerials: boolean
  reason: StockReorderReason
  /** In the scope. */
  onHand: string
  /** As `StockItemInsight.onOrder`: already on open draft purchases (and draft transfers in). */
  onOrder: string
  reorderLevel: string
  usagePerDay: string
  daysOfCover: string | null
  /**
   * What brings the stock up to the reorder level plus the target days of usage: reorder level + usage per day
   * × target days − on hand − on order, above zero, rounded up to a whole unit (to 3 decimals when the unit
   * allows fractions). So an item ordered as suggested ends above its reorder level, and what is already on
   * order is not ordered twice. With `warehouseId`, the level counts only when that warehouse has a balance
   * row for the item.
   */
  suggestedQuantity: string
  /**
   * With `warehouseId`: the other active warehouses holding the item, the largest first (a transfer may do
   * instead of a purchase). Empty over all warehouses.
   */
  elsewhere: { warehouse: ProductWarehouseRef; onHand: string }[]
  /** Only with `purchasing.read` over all purchases (ADR-025); null otherwise or when never bought. */
  lastPurchase: StockLastPurchase | null
  /** suggestedQuantity × the last purchase's unit cost when shown, else × the average cost (3 dp). */
  estimatedCost: string
}

/**
 * The items to reorder (INVENTORY_ASSISTANT.md §3.1): out or below zero with usage, `STOCK_COVER_WARN_DAYS` of
 * cover or fewer, or at or below a reorder level above zero; only those whose suggested quantity is above zero
 * (an item fully covered by what is on order is left out).
 * With `warehouseId`, an item without usage there is listed only when that warehouse holds or once held it (it
 * has a balance row): levels are per variant, so a branch is not asked to stock what it never carried.
 * Ordered by reason (as `STOCK_REORDER_REASONS`), then the fewest days of cover, then the name.
 */
export interface StockReorderSuggestions {
  asOf: string
  warehouse: ProductWarehouseRef | null
  usageDays: number
  targetDays: number
  items: StockReorderRow[]
  page: number
  pageSize: number
  total: number
  /** Σ estimatedCost of every row, not just this page. */
  estimatedCost: string
}

// ── a transfer between two warehouses, by the rule (`GET /stock/insights/transfer-plan`) ────────────────────

export const stockTransferPlanQuerySchema = z
  .object({
    fromWarehouseId: z.uuid(),
    toWarehouseId: z.uuid(),
    targetDays: z.coerce
      .number()
      .int()
      .min(STOCK_REORDER_TARGET_MIN_DAYS)
      .max(STOCK_REORDER_TARGET_MAX_DAYS)
      .default(STOCK_REORDER_TARGET_DAYS),
  })
  .refine((v) => v.fromWarehouseId !== v.toWarehouseId, {
    path: ['toWarehouseId'],
    message: 'Choose a different warehouse to transfer to',
  })
export type StockTransferPlanQuery = z.input<typeof stockTransferPlanQuerySchema>

export interface StockTransferPlanRow {
  variant: ReportVariant
  allowFractions: boolean
  trackSerials: boolean
  /**
   * What the destination needs: its reorder row's `suggestedQuantity` (the reorder rule in that warehouse, net
   * of what is already on order or on a draft transfer into it).
   */
  needed: string
  /** On hand at the source… */
  sourceOnHand: string
  /**
   * …and what it can spare: on hand − what the reorder rule wants the source itself to hold (the reorder level
   * plus its own usage per day × target days), never below zero. A transfer never takes the source below its
   * own reorder level.
   */
  spare: string
  /**
   * min(needed, spare), above zero; rounded down to a whole unit (3 decimals when the unit allows fractions),
   * so the source is never left short of its own target.
   */
  quantity: string
}

/**
 * What a transfer from one active warehouse to another would carry by the reorder rule: the destination's
 * reorder rows that the source can spare something of, the largest need first; at most 100 rows (`total` counts
 * them all). Both warehouses must be active (404 otherwise).
 */
export interface StockTransferPlan {
  asOf: string
  from: ProductWarehouseRef
  to: ProductWarehouseRef
  usageDays: number
  targetDays: number
  items: StockTransferPlanRow[]
  total: number
}

// ── a variant's reorder level (`PUT /product-variants/:id/reorder-level`, products.update) ──────────────────

/**
 * Sets one variant's reorder level without resending the product (IA1 decision 9). `version` is the product's;
 * omitted, the level is set whatever the product's version (the assistant's card and inline edits). The
 * product's version goes up and the change is audited on the product.
 */
export const variantReorderLevelSchema = z.object({
  reorderLevel: quantitySchema.refine((v) => Number(v) >= 0, 'The reorder level cannot be negative'),
  version: versionSchema.optional(),
})
export type VariantReorderLevelInput = z.input<typeof variantReorderLevelSchema>

export interface VariantReorderLevel {
  productId: string
  variantId: string
  /** "Product — Variant", as reports print it. */
  label: string
  labelAr: string | null
  sku: string | null
  /** The level before and after. */
  previousReorderLevel: string
  reorderLevel: string
  /** The product's new version. */
  version: number
}
